import React from 'react';

interface MetricsDisplayProps {
  neighbors: number;
  vpcs: number;
  rts: number;
  prefixes: number;
  direction: 'INBOUND' | 'OUTBOUND';
  lastUpdate: Date | null;
  /**
   * Every BGP session is down while the reflector is up (`deriveBgpSessionState`). Surfaces an
   * explicit zero next to the neighbor count — otherwise "N Neighbors" with no prefixes reads
   * as an empty topology rather than a peering outage.
   */
  allSessionsDown?: boolean;
}

/** "1 VPC" / "2 VPCs" — the strip reads as prose, so counts of one must not say "1 VPCs". */
const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

/**
 * Read-only counter strip for the control bar. Deliberately plain inline text rather than a
 * card: it sits *inside* a toolbar, so a nested bordered/shadowed container would both look
 * wrong and cost the row extra height.
 */
export const MetricsDisplay: React.FC<MetricsDisplayProps> = ({
  neighbors,
  vpcs,
  rts,
  prefixes,
  direction,
  lastUpdate,
  allSessionsDown = false
}) => {
  const isOutbound = direction === 'OUTBOUND';

  return (
    <div className="flex items-center gap-x-3 whitespace-nowrap text-body-small text-semantic-text-secondary">
      <span>{pluralize(neighbors, 'Neighbor')}</span>
      {allSessionsDown && <span className="text-semantic-text-danger">0 Established</span>}
      <span>{pluralize(vpcs, 'VPC')}</span>
      <span>{pluralize(rts, isOutbound ? 'Subnet' : 'RT')}</span>
      {/* "Announced" is not a countable noun, so it never pluralizes. */}
      <span>{isOutbound ? `${prefixes} Announced` : pluralize(prefixes, 'Prefix', 'Prefixes')}</span>
      {lastUpdate && <span>Updated: {lastUpdate.toLocaleTimeString()}</span>}
    </div>
  );
};
